import { DomainEvent } from '../../../project-management/domain/events/DomainEvent';
import { ArtifactStatus, ConformanceScore, FlowStatus } from '../value-objects';
import { FlowStateError, ArtifactStateError, ArtifactNotFoundError } from '../errors/DesignImplementationDomainError';
import { 
  FlowCreated, 
  ArtifactAdded, 
  ConformanceScoreUpdated, 
  FlowCompleted,
  ArtifactStatusChanged 
} from '../events/DesignImplementationEvents';

/**
 * Artifact Entity within the Flow aggregate
 */
export interface Artifact {
  id: string;
  name: string;
  type: 'Package' | 'Component' | 'Service' | 'Module' | 'Documentation';
  version?: string;
  location: {
    type: 'npm' | 'file' | 'service' | 'repository';
    path: string;
    url?: string;
    registry?: string;
  };
  status: ArtifactStatus;
  conformanceScore?: ConformanceScore;
  healthStatus: {
    buildStatus: 'success' | 'failure' | 'pending' | 'not-applicable';
    testStatus: 'passing' | 'failing' | 'partial' | 'not-applicable';
    deploymentStatus: 'deployed' | 'pending' | 'failed' | 'not-applicable';
    lastHealthCheck: Date;
  };
  createdBy: string;
  createdAt: Date;
  lastModified: Date;
  tags: string[];
}

/**
 * Design Implementation Flow Aggregate - Following GTD Project Pattern
 * 
 * Tracks the coordination between design specifications and their implementation
 * through artifacts, maintaining conformance scores and progress metrics.
 * 
 * Core Principles:
 * - Every flow must be linked to a GTD project
 * - Artifacts track conformance to meta-models and design coverage
 * - Flow provides progress metrics back to GTD system
 */
export class DesignImplementationFlow {
  private domainEvents: DomainEvent[] = [];
  private _artifacts: Artifact[] = [];
  private _status: FlowStatus = FlowStatus.draft();
  private _completedAt?: Date;

  constructor(
    public readonly id: string,
    public readonly projectId: string,
    public readonly createdBy: string,
    public readonly createdAt: Date = new Date(),
    public readonly metaModelBinding?: {
      metamodelId: string;
      boundAt: Date;
      validationStatus: 'valid' | 'invalid' | 'pending';
    },
    public readonly designSpecification?: {
      designId: string;
      designType: 'Component' | 'Page' | 'Pattern' | 'System';
      domainContext: string;
    }
  ) {
    this.validateFlow(projectId);
  }

  /**
   * Create a new design implementation flow
   */
  static create(
    projectId: string,
    createdBy: string,
    metaModelBinding?: {
      metamodelId: string;
      boundAt: Date;
      validationStatus: 'valid' | 'invalid' | 'pending';
    },
    designSpecification?: {
      designId: string;
      designType: 'Component' | 'Page' | 'Pattern' | 'System';
      domainContext: string;
    }
  ): DesignImplementationFlow {
    const id = this.generateId();
    const flow = new DesignImplementationFlow(
      id, 
      projectId, 
      createdBy, 
      new Date(),
      metaModelBinding,
      designSpecification
    );

    flow.addDomainEvent(new FlowCreated(id, projectId, createdBy));
    return flow;
  }

  /**
   * Add artifact to the flow
   */
  addArtifact(
    name: string,
    type: 'Package' | 'Component' | 'Service' | 'Module' | 'Documentation',
    location: {
      type: 'npm' | 'file' | 'service' | 'repository';
      path: string;
      url?: string;
      registry?: string;
    },
    createdBy: string,
    version?: string,
    tags: string[] = []
  ): string {
    if (this.isCompleted()) {
      throw new FlowStateError(
        'Cannot add artifacts to completed flows',
        this.id,
        this._status.toString()
      );
    }

    const artifactId = this.generateArtifactId();
    const artifact: Artifact = {
      id: artifactId,
      name,
      type,
      version,
      location,
      status: ArtifactStatus.planning(),
      healthStatus: {
        buildStatus: 'not-applicable',
        testStatus: 'not-applicable',
        deploymentStatus: 'not-applicable',
        lastHealthCheck: new Date()
      },
      createdBy,
      createdAt: new Date(),
      lastModified: new Date(),
      tags
    };

    this._artifacts.push(artifact);
    
    this.addDomainEvent(new ArtifactAdded(
      this.id, 
      artifactId, 
      name, 
      type,
      createdBy
    ));

    return artifactId;
  }

  /**
   * Update artifact status
   */
  updateArtifactStatus(artifactId: string, newStatus: string, updatedBy: string): void {
    const artifact = this.findArtifact(artifactId);
    const currentStatus = artifact.status.toString();

    if (!artifact.status.canTransitionTo(newStatus)) {
      throw new ArtifactStateError(
        `Cannot transition from ${currentStatus} to ${newStatus}`,
        artifactId,
        currentStatus
      );
    }

    const previousStatus = artifact.status.toString();
    artifact.status = ArtifactStatus.fromString(newStatus);
    artifact.lastModified = new Date();

    this.addDomainEvent(new ArtifactStatusChanged(
      this.id,
      artifactId,
      previousStatus,
      newStatus,
      updatedBy
    ));
  }

  /**
   * Update conformance score for an artifact
   */
  updateConformanceScore(
    artifactId: string, 
    metaModelScore: number, 
    designCoverageScore: number,
    confidence: 'high' | 'medium' | 'low',
    updatedBy: string
  ): void {
    const artifact = this.findArtifact(artifactId);
    const previousScore = artifact.conformanceScore?.overallScore || 0;
    
    artifact.conformanceScore = ConformanceScore.create(
      metaModelScore, 
      designCoverageScore, 
      confidence
    );
    artifact.lastModified = new Date();

    const newScore = artifact.conformanceScore.overallScore;
    
    this.addDomainEvent(new ConformanceScoreUpdated(
      this.id,
      artifactId,
      previousScore,
      newScore,
      updatedBy
    ));
  }

  /**
   * Activate the flow
   */
  activate(): void {
    if (!this._status.canTransitionTo('active')) {
      throw new FlowStateError(
        `Cannot activate flow from ${this._status.toString()} state`,
        this.id,
        this._status.toString()
      );
    }

    this._status = FlowStatus.active();
  }

  /**
   * Complete the flow
   */
  complete(completedBy?: string): void {
    if (!this._status.canTransitionTo('completed')) {
      throw new FlowStateError(
        `Cannot complete flow from ${this._status.toString()} state`,
        this.id,
        this._status.toString()
      );
    }

    this._status = FlowStatus.completed();
    this._completedAt = new Date();

    const metrics = this.getProgressMetrics();
    
    this.addDomainEvent(new FlowCompleted(
      this.id,
      this.projectId,
      metrics.totalArtifacts,
      metrics.averageConformance,
      completedBy || this.createdBy
    ));
  }

  /**
   * Abandon the flow
   */
  abandon(): void {
    if (!this._status.canTransitionTo('abandoned')) {
      throw new FlowStateError(
        `Cannot abandon flow from ${this._status.toString()} state`,
        this.id,
        this._status.toString()
      );
    }

    this._status = FlowStatus.abandoned();
  }

  /**
   * Get progress metrics for GTD integration
   */
  getProgressMetrics(): {
    totalArtifacts: number;
    completedArtifacts: number;
    artifactsInProgress: number;
    averageConformance: number;
    averageDesignCoverage: number;
  } {
    const total = this._artifacts.length;
    const completed = this._artifacts.filter(a => a.status.toString() === 'production').length;
    const inProgress = this._artifacts.filter(a => 
      ['development', 'review'].includes(a.status.toString())
    ).length;

    const conformanceScores = this._artifacts
      .map(a => a.conformanceScore?.overallScore || 0)
      .filter(score => score > 0);
    
    const averageConformance = conformanceScores.length > 0 
      ? conformanceScores.reduce((sum, score) => sum + score, 0) / conformanceScores.length 
      : 0;

    const designScores = this._artifacts
      .map(a => a.conformanceScore?.designCoverageScore || 0)
      .filter(score => score > 0);
    
    const averageDesignCoverage = designScores.length > 0
      ? designScores.reduce((sum, score) => sum + score, 0) / designScores.length
      : 0;

    return {
      totalArtifacts: total,
      completedArtifacts: completed,
      artifactsInProgress: inProgress,
      averageConformance: Math.round(averageConformance),
      averageDesignCoverage: Math.round(averageDesignCoverage)
    };
  }

  /**
   * Get artifacts that need attention
   */
  getBlockedArtifacts(): Array<{
    artifactId: string;
    artifactName: string;
    blockReason: string;
    severity: 'critical' | 'high' | 'medium' | 'low';
  }> {
    const blocked: Array<{
      artifactId: string;
      artifactName: string;
      blockReason: string;
      severity: 'critical' | 'high' | 'medium' | 'low';
    }> = [];

    this._artifacts.forEach(artifact => {
      // Check for build failures
      if (artifact.healthStatus.buildStatus === 'failure') {
        blocked.push({
          artifactId: artifact.id,
          artifactName: artifact.name,
          blockReason: 'Build failing',
          severity: 'critical'
        });
      }

      // Check for poor conformance
      if (artifact.conformanceScore && artifact.conformanceScore.needsImprovement()) {
        blocked.push({
          artifactId: artifact.id,
          artifactName: artifact.name,
          blockReason: `Low conformance score: ${artifact.conformanceScore.overallScore}%`,
          severity: artifact.conformanceScore.overallScore < 50 ? 'high' : 'medium'
        });
      }

      // Check for stale artifacts
      const daysSinceUpdate = Math.floor(
        (new Date().getTime() - artifact.lastModified.getTime()) / (1000 * 60 * 60 * 24)
      );
      if (daysSinceUpdate > 7 && artifact.status.toString() === 'development') {
        blocked.push({
          artifactId: artifact.id,
          artifactName: artifact.name,
          blockReason: `No updates for ${daysSinceUpdate} days`,
          severity: daysSinceUpdate > 14 ? 'medium' : 'low'
        });
      }
    });

    return blocked;
  }

  // State queries
  isCompleted(): boolean {
    return this._status.isCompleted();
  }

  isActive(): boolean {
    return this._status.isActive();
  }

  // Getters
  get status(): FlowStatus {
    return this._status;
  }

  get artifacts(): Artifact[] {
    return [...this._artifacts];
  }

  get completedAt(): Date | undefined {
    return this._completedAt;
  }

  // Event sourcing support
  getDomainEvents(): DomainEvent[] {
    return [...this.domainEvents];
  }

  clearDomainEvents(): void {
    this.domainEvents = [];
  }

  // Private methods
  private findArtifact(artifactId: string): Artifact {
    const artifact = this._artifacts.find(a => a.id === artifactId);
    if (!artifact) {
      throw new ArtifactNotFoundError(artifactId, this.id);
    }
    return artifact;
  }

  private validateFlow(projectId: string): void {
    if (!projectId?.trim()) {
      throw new FlowStateError('Project ID is required', '', 'unknown');
    }
  }

  private addDomainEvent(event: DomainEvent): void {
    this.domainEvents.push(event);
  }

  private static generateId(): string {
    return `flow-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
  }

  private generateArtifactId(): string {
    return `artifact-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
  }
}